import { Api } from '../api';
import { users } from '../endpoints';
import type { ApiResponse, ApiPaginate } from '@shared/api/models';
import type {
  AddUserAccountsRequest,
  AddUserCardsRequest,
  CreateUserRequest,
  DeleteUserAccountsRequest,
  DeleteUserCardsRequest,
  GenerateCodeRequest,
  GenerateCodeResponse,
  GetAccountUsersRequest,
  GetAccountUsersResponse,
  UpdatePasswordRequest,
  UserProfileResponse,
  UsersRequest,
  UsersResponse,
  UserUpdateRequest,
  UserUpdateWithIdRequest,
} from './models';

export class UsersApi extends Api {
  static getUsers = (request: UsersRequest, signal?: AbortSignal) =>
    this.get<ApiPaginate<UsersResponse[]>>({ url: users.getUsers(), queryParams: request, signal });

  static getUser = async (uId: string, signal?: AbortSignal) => {
    const result = await this.get<ApiResponse<UserProfileResponse>>({ url: users.getUser(uId), signal });
    return result.data;
  };

  static updateUser = async (request: UserUpdateWithIdRequest, signal?: AbortSignal) => {
    const result = await this.put<UserUpdateRequest, ApiResponse<UserProfileResponse>>({
      url: users.updateUser(request.id),
      data: request.request as unknown as UserUpdateRequest,
      signal,
    });
    return result.data;
  };

  static lockUser = (uId: string, signal?: AbortSignal) => this.put<null, ApiResponse<UserProfileResponse>>({ url: users.lockUser(uId), signal });

  static unLockUser = (uId: string, signal?: AbortSignal) => this.put<null, ApiResponse<UserProfileResponse>>({ url: users.unLockUser(uId), signal });

  static deleteUser = (uId: string) => this.delete<null, ApiResponse<null>>({ url: users.deleteUser(uId) });

  static updatePassword = async (update: UpdatePasswordRequest, signal?: AbortSignal) => {
    const result = await this.put<UpdatePasswordRequest, ApiResponse<UserProfileResponse>>({
      url: users.updatePassword(),
      data: { password: update.password, confirmPassword: update.confirmPassword, userId: update.userId },
      signal,
    });
    return result.success;
  };

  static addUserAccounts = (request: AddUserAccountsRequest, signal?: AbortSignal) =>
    this.put<AddUserAccountsRequest, ApiResponse<UserProfileResponse>>({
      url: users.addUserAccounts(),
      data: { userIds: request.userIds, accountIds: request.accountIds },
      signal,
    });

  static deleteUserAccounts = (request: DeleteUserAccountsRequest, signal?: AbortSignal) =>
    this.delete<DeleteUserAccountsRequest, ApiResponse<UserProfileResponse>>({
      url: users.deleteUserAccounts(),
      data: { userIds: request.userIds, accountIds: request.accountIds },
      signal,
    });

  static generateCode = (request: GenerateCodeRequest, signal?: AbortSignal) =>
    this.put<GenerateCodeRequest, ApiResponse<GenerateCodeResponse>>({ url: users.degenerateCode(), data: request, signal });

  static addUserCards = (request: AddUserCardsRequest, signal?: AbortSignal) =>
    this.put<AddUserCardsRequest, ApiResponse<UserProfileResponse>>({
      url: users.addUserCards(),
      data: { userIds: request.userIds, cardIds: request.cardIds },
      signal,
    });

  static deleteUserCards = (request: DeleteUserCardsRequest, signal?: AbortSignal) =>
    this.delete<DeleteUserCardsRequest, ApiResponse<UserProfileResponse>>({
      url: users.deleteUserCards(),
      data: { userIds: request.userIds, cardIds: request.cardIds },
      signal,
    });

  static addUser = (request: CreateUserRequest) => this.post<CreateUserRequest, ApiResponse<UsersResponse>>({ url: users.getUsers(), data: request });

  static getAccountUsers = (request: GetAccountUsersRequest, signal?: AbortSignal) =>
    this.get<ApiPaginate<GetAccountUsersResponse[]>>({ url: users.getAccountUsers(), queryParams: request, signal });
}
